import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import * as Sentry from '@sentry/ember';
import type IntlService from 'ember-intl/services/intl';
import config from 'walden-trace-app/config/environment';
import type SessionService from 'walden-trace-app/services/session';
import schema from 'walden-trace-app/utils/graphql/schema.graphql';
import { matchLocales } from 'walden-trace-app/utils/match-locales';
import worker from 'walden-trace-app/utils/msw';

export default class ApplicationRoute extends Route {
  @service('intl') declare intlService: IntlService;
  @service('router') declare routerService: RouterService;
  @service('session') declare sessionService: SessionService;

  async beforeModel(transition: Transition) {
    this.setupIntl();
    this.setupSentry();
    await this.setupMsw();

    // Check if guest token is present in the URL (e.g. coming from email)
    const guestToken = transition.to?.queryParams?.['guestToken'] as string | undefined;
    if (guestToken) {
      this.sessionService.setGuestToken(guestToken);
    }
  }

  setupIntl() {
    const matchedLocales = matchLocales({
      browserLocales: [...navigator.languages],
      supportedLocales: this.intlService.locales,
      defaultLocale: config.defaultLocale,
    });

    this.intlService.setLocale(matchedLocales);
  }

  setupSentry() {
    Sentry.setContext('app', {
      locale: this.intlService.primaryLocale,
    });
  }

  setupMsw() {
    if (config.environment === 'test' || !config.msw.isEnabled) {
      return;
    }
    return worker.start(schema);
  }
}
